import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import AnnouncementForm, {
	AnnouncementFormValues
} from "../../components/AnnouncementForm/AnnouncementForm";
import useCreateAnnouncement from "../../hooks/useCreateAnnouncement";
import useSendAnnouncement from "../../hooks/useSendAnnouncement";
import useScheduleAnnouncement from "../../hooks/useScheduleAnnouncement";
import { adaptCreateAnnouncementPayload } from "../../adapters/adaptAnnouncementPayload";
import { formValuesToAnnouncement } from "../../adapters/formValuesToAnnouncement";

const EMPTY_DEFAULTS: AnnouncementFormValues = {
	notificationType: "push",
	title: "",
	body: "",
	heroImage: null,
	pushTitle: "",
	pushBody: "",
	audienceType: "everyone",
	audiencePlatform: "both",
	audienceUserIds: [],
	audienceGroupIds: [],
	hasSchedule: false,
	scheduledDate: "",
	scheduledTime: "",
	hasExpiry: false,
	expiryDate: "",
	expiryTime: "",
	blockFiles: [],
	contentBlocks: []
};

const CreateAnnouncement = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { mutate: createAnnouncement, isLoading: isCreating } =
		useCreateAnnouncement();
	const { mutate: sendAnnouncement, isLoading: isSending } =
		useSendAnnouncement();
	const { mutate: scheduleAnnouncement, isLoading: isScheduling } =
		useScheduleAnnouncement();
	const [confirmKind, setConfirmKind] = useState<"send" | "schedule" | null>(
		null
	);
	const [pendingData, setPendingData] = useState<{
		data: AnnouncementFormValues;
		imageRemoved: boolean;
	} | null>(null);

	const handleSaveDraft = (
		data: AnnouncementFormValues,
		_imageRemoved: boolean
	) => {
		const announcementData = formValuesToAnnouncement(data);
		const payload = adaptCreateAnnouncementPayload(
			announcementData,
			data.heroImage
		);
		createAnnouncement(payload, {
			onSuccess: async () => {
				await queryClient.invalidateQueries(["announcements"]);
				navigate("/announcements");
			}
		});
	};

	const handleSendNow = (
		data: AnnouncementFormValues,
		imageRemoved: boolean
	) => {
		setPendingData({ data, imageRemoved });
		setConfirmKind("send");
	};

	const handleSchedule = (
		data: AnnouncementFormValues,
		imageRemoved: boolean
	) => {
		setPendingData({ data, imageRemoved });
		setConfirmKind("schedule");
	};

	const closeConfirm = () => {
		setConfirmKind(null);
		setPendingData(null);
	};

	const confirmSubmit = () => {
		if (!pendingData || !confirmKind) return;
		const { data } = pendingData;
		const announcementData = formValuesToAnnouncement(data);
		const payload = adaptCreateAnnouncementPayload(
			announcementData,
			data.heroImage
		);
		const finish = async () => {
			closeConfirm();
			await queryClient.invalidateQueries(["announcements"]);
			navigate("/announcements");
		};
		createAnnouncement(payload, {
			onSuccess: (created) => {
				if (confirmKind === "send") {
					sendAnnouncement(created.id, {
						onSuccess: finish,
						onError: closeConfirm
					});
				} else {
					scheduleAnnouncement(created.id, {
						onSuccess: finish,
						onError: closeConfirm
					});
				}
			},
			onError: closeConfirm
		});
	};

	const getConfirmMessage = () => {
		if (!pendingData) return "";
		const { data } = pendingData;
		const audience =
			data.audienceType === "everyone"
				? "all users"
				: data.audienceType === "platform"
					? `${data.audiencePlatform === "both" ? "all" : data.audiencePlatform} users`
					: data.audienceType === "groups"
						? `${data.audienceGroupIds.length} selected group${data.audienceGroupIds.length !== 1 ? "s" : ""}`
						: `${data.audienceUserIds.length} selected user${data.audienceUserIds.length !== 1 ? "s" : ""}`;

		if (confirmKind === "schedule") {
			return `Schedule this announcement for ${data.scheduledDate} at ${data.scheduledTime || "00:00"} to ${audience}?`;
		}
		return `Send this announcement now to ${audience}?`;
	};

	const isBusy = isCreating || isSending || isScheduling;

	return (
		<Page>
			<AnnouncementForm
				defaultValues={EMPTY_DEFAULTS}
				onSubmit={handleSaveDraft}
				onSchedule={handleSchedule}
				onSendNow={handleSendNow}
				isSubmitting={isCreating}
				isScheduling={isScheduling}
				isSending={isSending}
			/>
			<ConfirmationModal
				isOpen={confirmKind !== null}
				title={
					confirmKind === "schedule"
						? "Schedule announcement?"
						: "Send announcement?"
				}
				description={getConfirmMessage()}
				onCancel={() => {
					if (!isBusy) closeConfirm();
				}}
				cancelBtnProps={{ disabled: isBusy }}
				onSubmit={confirmSubmit}
				submitBtnProps={{ isLoading: isBusy }}
			/>
		</Page>
	);
};

export default CreateAnnouncement;
